/**
 * @file App.tsx
 * @description Defines the main application component that sets up routing for different pages, manages WebSocket connections for real-time updates, and initializes notifications. It uses React Router for navigation and custom hooks for WebSocket and notification handling.
 * @author Son Nguyen <hoangson091104@gmail.com>
 */

import { BrowserRouter, Routes, Route } from "react-router-dom";
import { useCallback, useEffect, useState } from "react";
import { Layout } from "./components/Layout";
import { CommandCenter } from "./pages/CommandCenter";
import { Inbox } from "./pages/Inbox";
import { Today } from "./pages/Today";
import { Tasks } from "./pages/Tasks";
import { Routines } from "./pages/Routines";
import { Trakcja } from "./pages/Trakcja";
import { Ekosystem } from "./pages/Ekosystem";
import { EkosystemDetail } from "./pages/EkosystemDetail";
import { Komendy } from "./pages/Komendy";
import { KomendyDetail } from "./pages/KomendyDetail";
import { Login } from "./pages/Login";
import { Dashboard } from "./pages/Dashboard";
import { KanbanBoard } from "./pages/KanbanBoard";
import { Sessions } from "./pages/Sessions";
import { SessionDetail } from "./pages/SessionDetail";
import { ActivityFeed } from "./pages/ActivityFeed";
import { Analytics } from "./pages/Analytics";
import { Workflows } from "./pages/Workflows";
import { Settings } from "./pages/Settings";
import { CcConfig } from "./pages/CcConfig";
import { Run } from "./pages/Run";
import { Infrastructure } from "./pages/Infrastructure";
import { NotFound } from "./pages/NotFound";
import { useWebSocket } from "./hooks/useWebSocket";
import { useNotifications } from "./hooks/useNotifications";
import { eventBus } from "./lib/eventBus";
import { api } from "./lib/api";
import type { AuthMe } from "./lib/api";
import type { WSMessage } from "./lib/types";

export default function App() {
  const onMessage = useCallback((msg: WSMessage) => {
    eventBus.publish(msg);
  }, []);

  const { connected } = useWebSocket(onMessage);
  useNotifications();

  // OAuth gate (no-op when AUTH_MODE is off — /api/auth/me returns authEnabled:false).
  const [authChecked, setAuthChecked] = useState(false);
  const [authState, setAuthState] = useState<AuthMe>({ authEnabled: false, user: null });
  useEffect(() => {
    let cancelled = false;
    api.auth
      .me()
      .then((m) => {
        if (!cancelled) setAuthState(m);
      })
      .catch(() => {
        /* network error → treat as open; data APIs stay the source of truth */
      })
      .finally(() => {
        if (!cancelled) setAuthChecked(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (!authChecked) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-surface-0 text-sm text-gray-500">
        Ładowanie…
      </div>
    );
  }
  if (authState.authEnabled && !authState.user) {
    return <Login />;
  }

  return (
    <BrowserRouter>
      <Routes>
        <Route element={<Layout wsConnected={connected} />}>
          <Route index element={<CommandCenter />} />
          <Route path="command-center" element={<CommandCenter />} />
          <Route path="inbox" element={<Inbox />} />
          <Route path="today" element={<Today />} />
          <Route path="tasks" element={<Tasks />} />
          <Route path="routines" element={<Routines />} />
          <Route path="trakcja" element={<Trakcja />} />
          <Route path="ekosystem" element={<Ekosystem />} />
          <Route path="ekosystem/:id" element={<EkosystemDetail />} />
          <Route path="komendy" element={<Komendy />} />
          <Route path="komendy/:id" element={<KomendyDetail />} />
          <Route path="dashboard" element={<Dashboard />} />
          <Route path="kanban" element={<KanbanBoard />} />
          <Route path="sessions" element={<Sessions />} />
          <Route path="sessions/:id" element={<SessionDetail />} />
          <Route path="activity" element={<ActivityFeed />} />
          <Route path="analytics" element={<Analytics />} />
          <Route path="workflows" element={<Workflows />} />
          <Route path="cc-config" element={<CcConfig />} />
          <Route path="run" element={<Run />} />
          <Route path="infrastructure" element={<Infrastructure />} />
          <Route path="settings" element={<Settings />} />
          <Route path="*" element={<NotFound />} />
        </Route>
      </Routes>
    </BrowserRouter>
  );
}
